// F1 App-Shell (TES-948): Mobile BottomNav (screen-09).
// fixed bottom-0, border-t border-outline-variant, md:hidden. Haupt-Slots aus
// `navigation` (Dashboard/Tasks/Inspections/Defects). Aktiver Slot = Pill
// (bg-primary-container text-primary-container-foreground rounded-full).
// Center-FAB-Variante (screen-06) hinter Prop, default aus (App hat keinen
// globalen FAB-Flow). Safe-Area unten via env(safe-area-inset-bottom).
// Slot-Rendering in Sub-Komponenten zerlegt, damit keine Funktion die
// fallow-CRAP-Schwelle reisst — Verhalten/Markup unveraendert.
import { Link, useLocation } from "react-router";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ShellModel } from "./useShellModel";
import { navigation, type NavItem } from "./navConfig";
import { getNavBadgeInfo } from "./navBadges";
import { NavBadgeDot } from "./NavBadge";

interface BottomNavProps {
  model: ShellModel;
  /** screen-06-Variante: zentraler raised FAB. Default aus. */
  showCenterFab?: boolean;
  onCenterFabClick?: () => void;
}

// Haupt-Slots der BottomNav: Dashboard, Tasks, Inspections, Defects (Mock screen-09)
const BOTTOM_SLOTS = ["/", "/tasks", "/inspections", "/defects"];

// Slot ist sichtbar, wenn er zu den Haupt-Slots gehoert und die Rolle passt (1:1 aus AppLayout).
function isVisibleSlot(item: NavItem, roles: string[]): boolean {
  if (!BOTTOM_SLOTS.includes(item.href)) return false;
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.some((r) => roles.includes(r));
}

function slotClass(isActive: boolean): string {
  return cn(
    "flex flex-col items-center justify-center px-3 py-1 transition-colors min-w-[56px]",
    isActive
      ? "bg-primary-container text-primary-container-foreground rounded-full px-4"
      : "text-on-surface-variant hover:bg-surface-container-high rounded-full",
  );
}

// Zentraler raised FAB (nur wenn aktiviert und an der Mittel-Position).
function CenterFab({ model, onClick }: { model: ShellModel; onClick?: () => void }) {
  return (
    <div className="flex justify-center -mt-8">
      <button
        type="button"
        onClick={onClick}
        aria-label={model.t("common.create")}
        className="w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg border-4 border-surface flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="h-6 w-6" />
      </button>
    </div>
  );
}

// Inhalt eines Slots: Icon (+ Badge-Dot) + Label.
function SlotContent({ item, model }: { item: NavItem; model: ShellModel }) {
  const badge = getNavBadgeInfo(item.href, model);
  return (
    <>
      <div className="relative">
        <item.icon className="h-5 w-5" />
        <NavBadgeDot badge={badge} className="-top-1.5 -right-2" />
      </div>
      <span className="font-label-caps text-label-caps mt-0.5">{model.t(item.nameKey)}</span>
    </>
  );
}

// Ein BottomNav-Slot inkl. optionalem vorangestelltem FAB; offline = nicht-klickbar.
function BottomNavSlot({
  item,
  model,
  fab,
}: {
  item: NavItem;
  model: ShellModel;
  fab: React.ReactNode;
}) {
  const location = useLocation();
  const isActive = location.pathname === item.href;
  const isOfflineDisabled = !model.isOnline && !item.offlineEnabled;
  const baseClass = slotClass(isActive);

  if (isOfflineDisabled) {
    return (
      <span className="contents">
        {fab}
        <div
          className={cn(baseClass, "opacity-40 cursor-not-allowed pointer-events-none")}
          aria-disabled="true"
        >
          <SlotContent item={item} model={model} />
        </div>
      </span>
    );
  }

  return (
    <span className="contents">
      {fab}
      <Link to={item.href} className={baseClass} aria-current={isActive ? "page" : undefined}>
        <SlotContent item={item} model={model} />
      </Link>
    </span>
  );
}

export function BottomNav({ model, showCenterFab = false, onCenterFabClick }: BottomNavProps) {
  const { roles } = model;

  // TES-1092: Nur im Tenant-Kontext (Guest-Mode) gibt es keine operative BottomNav —
  // global angemeldete super_admins sehen ihre per Rollen-Filter erlaubten Slots
  // (Dashboard/Prüfungen/Mängel), analog zur SideNav.
  if (model.isTenantContext) return null;

  const slots = navigation.filter((item) => isVisibleSlot(item, roles));
  if (slots.length === 0) return null;

  const fabIndex = Math.floor(slots.length / 2);

  return (
    <nav
      data-tour="bottom-nav"
      className="md:hidden fixed bottom-0 left-0 w-full z-50 flex justify-around items-center h-16 px-2 bg-surface border-t border-outline-variant pb-[env(safe-area-inset-bottom)]"
    >
      {slots.map((item, idx) => {
        const fab =
          showCenterFab && idx === fabIndex ? (
            <CenterFab key="__fab" model={model} onClick={onCenterFabClick} />
          ) : null;
        return <BottomNavSlot key={item.nameKey} item={item} model={model} fab={fab} />;
      })}
    </nav>
  );
}
